import React, { useState, useEffect } from "react";
import { useAuthStore } from "../../../store/authStore";
import { useTicketStore } from "../../../store/ticketStore";
import {
	CalendarIcon,
	XMarkIcon,
	MagnifyingGlassIcon,
	XCircleIcon,
	PlusIcon,
	TrashIcon,
} from "@heroicons/react/24/outline";
import MaskingModal from "../Register/MaskingModal";
import RegisterDetailWithImageModal from "./RegisterDetailWithImageModal";
import EditTicketModal from "./EditTicketModal";
import { IoTicketOutline } from "react-icons/io5";

export default function TicketStatus() {
	const { user } = useAuthStore();
	const {
		getAllTickets,
		isLoading: storeLoading,
		error,
		loadMaskData,
		deleteTickets,
	} = useTicketStore();

	// 상태 관리
	const [tickets, setTickets] = useState([]);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [searchKeyword, setSearchKeyword] = useState("");
	const [localLoading, setLocalLoading] = useState(false);
	const [selectedTickets, setSelectedTickets] = useState([]);

	// 미리보기 모달 상태
	const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
	const [previewImageUrl, setPreviewImageUrl] = useState("");

	// 마스킹 모달 상태
	const [isMaskingModalOpen, setIsMaskingModalOpen] = useState(false);
	const [selectedTicketId, setSelectedTicketId] = useState(null);
	const [selectedTicketImageUrl, setSelectedTicketImageUrl] = useState("");
	const [ticketsForMasking, setTicketsForMasking] = useState([]);

	// 편집 모달 상태
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [selectedTicketForEdit, setSelectedTicketForEdit] = useState(null);
	const [currentTicketIndex, setCurrentTicketIndex] = useState(0);

	// 승차권 이미지 등록 모달 상태
	const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) {
			setIsPreviewModalOpen(false);
			setIsMaskingModalOpen(false);
		}
	};

	// 페이지 로드 시 기본 검색 기간 설정 (종료일: 오늘 / 시작일: 7일 전)
	useEffect(() => {
		const getDateOffset = (days) => {
			const date = new Date();
			date.setDate(date.getDate() + days);
			return date.toISOString().split("T")[0];
		};
		const defaultEnd = getDateOffset(0);
		const defaultStart = getDateOffset(-7);
		setStartDate(defaultStart);
		setEndDate(defaultEnd);

		handleSearch(defaultStart, defaultEnd);
	}, []);

	// 티켓 검색 함수
	const handleSearch = async (_startDate, _endDate) => {
		if (!_startDate || !_endDate) {
			alert("날짜를 선택해주세요.");
			return;
		}

		try {
			const result = await getAllTickets(
				_startDate,
				_endDate,
				searchKeyword
			);
			if (result.success) {
				setTickets(result.data);
				// 티켓 검색 시 선택된 티켓 초기화
				setSelectedTickets([]);
			}
		} catch (err) {
			console.error("티켓 검색 중 오류 발생:", err);
		}
	};

	// 티켓 선택 핸들러
	const handleTicketSelection = (e, ticketId) => {
		if (e.target.checked) {
			setSelectedTickets([...selectedTickets, ticketId]);
		} else {
			setSelectedTickets(selectedTickets.filter((id) => id !== ticketId));
		}
	};

	// 선택한 티켓 삭제 핸들러
	const handleDeleteSelectedTickets = async () => {
		if (selectedTickets.length === 0) {
			alert("삭제할 티켓을 선택해주세요.");
			return;
		}

		if (
			window.confirm(
				`선택하신 ${selectedTickets.length}개의 승차권 정보를 정말로 삭제하시겠습니까?`
			)
		) {
			try {
				setLocalLoading(true);
				const result = await deleteTickets(selectedTickets);
				setLocalLoading(false);

				if (result.success) {
					alert("삭제되었습니다.");
					// 현재 필터로 다시 조회
					handleSearch(startDate, endDate);
					// 선택 티켓 초기화
					setSelectedTickets([]);
				} else {
					alert(result.error || "티켓 삭제 중 오류가 발생했습니다.");
				}
			} catch (error) {
				console.error("티켓 삭제 오류:", error);
				setLocalLoading(false);
				alert("알 수 없는 문제가 발생했습니다.");
			}
		}
	};

	// 날짜 필터 초기화
	const handleClearDate = (type) => {
		if (type === "start") {
			setStartDate("");
		} else {
			setEndDate("");
		}
	};

	// 미리보기 버튼 클릭 핸들러
	const handlePreview = (ticket) => {
		if (ticket && ticket.image_url) {
			setPreviewImageUrl(ticket.image_url);
			setIsPreviewModalOpen(true);
		} else {
			alert("티켓 이미지를 찾을 수 없습니다.");
		}
	};

	// 상세보기 버튼 클릭 핸들러
	const handleEditTicket = (ticket) => {
		setSelectedTicketForEdit(ticket);
		setIsEditModalOpen(true);
	};

	// 티켓 마스킹 버튼 클릭 핸들러
	const handleOpenMaskingModal = async (ticket) => {
		try {
			// 티켓 ID 확인
			if (!ticket || !ticket.ticket_id) {
				alert("티켓 정보를 찾을 수 없습니다.");
				return;
			}

			setLocalLoading(true);

			// 티켓 ID와 이미지 URL 설정
			setSelectedTicketId(ticket.ticket_id);

			if (ticket.image_url) {
				// 이미지 URL 설정 (s3_url 또는 image_url 사용)
				const imageUrl = ticket.s3_url || ticket.image_url;
				setSelectedTicketImageUrl(imageUrl);

				// 현재 티켓을 포함한 티켓 배열 생성
				// MaskingModal에서 사용하는 형식에 맞게 배열의 배열로 전달
				setTicketsForMasking([[ticket]]);
				setCurrentTicketIndex(0);

				// 마스킹 모달 열기
				setIsMaskingModalOpen(true);
			} else {
				alert("티켓 이미지를 찾을 수 없습니다.");
			}
		} catch (error) {
			console.error("티켓 이미지 로드 중 오류 발생:", error);
			alert("티켓 이미지를 불러오는 데 실패했습니다.");
		} finally {
			setLocalLoading(false);
		}
	};

	return (
		<div className="py-6">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">
				승차권 등록 현황
			</h1>
			<p className="text-gray-600 mb-4">
				등록된 승차권을 관리하는 페이지입니다.
			</p>

			{/* 검색 필터 영역 */}
			<div className="bg-white shadow rounded-lg p-6 mb-6">
				<div className="flex flex-wrap items-center justify-between">
					<div className="flex flex-wrap items-center gap-4">
						{/* 시작 날짜 선택 */}
						<div className="relative">
							<div className="flex items-center">
								<label
									htmlFor="startDate"
									className="mr-2 text-sm font-medium text-gray-700"
								>
									시작일자
								</label>
								<div className="relative">
									<input
										type="date"
										id="startDate"
										value={startDate}
										onChange={(e) => {
											setStartDate(e.target.value);
										}}
										className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
									/>
									<CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
									{startDate && (
										<button
											onClick={() =>
												handleClearDate("start")
											}
											className="absolute right-2 top-1/2 transform -translate-y-1/2"
										>
											<XMarkIcon className="h-4 w-4 text-gray-400" />
										</button>
									)}
								</div>
							</div>
						</div>

						{/* 종료 날짜 선택 */}
						<div className="relative">
							<div className="flex items-center">
								<label
									htmlFor="endDate"
									className="mr-2 text-sm font-medium text-gray-700"
								>
									종료일자
								</label>
								<div className="relative">
									<input
										type="date"
										id="endDate"
										value={endDate}
										onChange={(e) =>
											setEndDate(e.target.value)
										}
										className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
									/>
									<CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
									{endDate && (
										<button
											onClick={() =>
												handleClearDate("end")
											}
											className="absolute right-2 top-1/2 transform -translate-y-1/2"
										>
											<XMarkIcon className="h-4 w-4 text-gray-400" />
										</button>
									)}
								</div>
							</div>
						</div>

						{/* 키워드 검색 */}
						<div className="relative w-56">
							<div className="flex items-center">
								<label
									htmlFor="searchKeyword"
									className="mr-2 text-sm font-medium text-gray-700 w-24"
								>
									출/도착역
								</label>
								<div className="relative flex-grow">
									<input
										type="text"
										id="searchKeyword"
										placeholder="역명 입력"
										value={searchKeyword}
										onChange={(e) =>
											setSearchKeyword(e.target.value)
										}
										className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
									/>
									<MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
								</div>
							</div>
						</div>

						{/* 검색 버튼 */}
						<button
							onClick={() => handleSearch(startDate, endDate)}
							className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition duration-150 ease-in-out"
						>
							<div className="flex items-center">
								<MagnifyingGlassIcon className="h-4 w-4 mr-1" />
								검색하기
							</div>
						</button>

						<div className="flex items-center space-x-3">
							{/* 승차권 이미지 등록 버튼 */}
							<button
								onClick={() => setIsRegisterModalOpen(true)}
								className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 transition-colors duration-200"
							>
								<PlusIcon className="h-4 w-4 mr-1.5" />
								승차권 등록
							</button>
							{/* 선택한 승차권 삭제 버튼 */}
							<button
								onClick={() => handleDeleteSelectedTickets()}
								className={`inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md transition-colors duration-200 ${
									selectedTickets.length === 0
										? "border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed"
										: "border-transparent text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400"
								}`}
								disabled={selectedTickets.length === 0}
							>
								<TrashIcon className="h-4 w-4 mr-1.5" />
								{selectedTickets.length > 0
									? `${selectedTickets.length}개 삭제`
									: "선택 삭제"}
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* 티켓 테이블 */}
			<div className="bg-white shadow rounded-lg overflow-hidden">
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									번호
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									티켓종류
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									예약/반환번호
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									출발일자
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									열차번호
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									출/도착역
								</th>
								<th
									scope="col"
									className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									배정현황
								</th>
								<th
									scope="col"
									className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									미리보기
								</th>
								<th
									scope="col"
									className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									상세보기
								</th>
								<th
									scope="col"
									className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
								>
									티켓 마스킹
								</th>
							</tr>
						</thead>
						<tbody
							className="bg-white divide-y divide-gray-200"
							id="main-tbody"
						>
							{tickets.length > 0 ? (
								tickets.map((ticket, index) => (
									<tr
										key={ticket.ticket_id}
										className="hover:bg-gray-50"
									>
										<td className="px-4 py-3 text-sm text-gray-900">
											<div className="flex items-center">
												<input
													type="checkbox"
													className="mr-2"
													checked={selectedTickets.includes(
														ticket.ticket_id
													)}
													onChange={(e) =>
														handleTicketSelection(
															e,
															ticket.ticket_id
														)
													}
												/>
												{index + 1}
											</div>
										</td>
										<td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
											{ticket.ticket_type}
										</td>
										<td className="px-4 py-3 text-sm text-gray-900">
											<div
												style={{
													whiteSpace: "pre-line",
												}}
											>
												{ticket.reservation_number}
											</div>
										</td>
										<td className="px-4 py-3 text-sm text-gray-900">
											<div
												style={{
													whiteSpace: "pre-line",
												}}
											>
												{ticket.departure_times}
											</div>
										</td>
										<td className="px-4 py-3 text-sm text-gray-900">
											<div
												style={{
													whiteSpace: "pre-line",
												}}
											>
												{ticket.train_codes}
											</div>
										</td>
										<td className="px-4 py-3 text-sm text-gray-900">
											<div
												style={{
													whiteSpace: "pre-line",
												}}
											>
												{(ticket.departure_arrival_stations || "")
													.split("\n")
													.map((station, idx) => (
														<div key={idx}>
															{station.replace(
																/ - /g,
																" → "
															)}
														</div>
													))}
											</div>
										</td>
										<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
											<div
												style={{
													whiteSpace: "pre-line",
												}}
											>
												{ticket.allocation_status}
											</div>
										</td>
										<td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900">
											<button
												onClick={() =>
													handlePreview(ticket)
												}
												className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
											>
												<svg
													className="w-3 h-3 mr-1"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth="2"
														d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
													></path>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth="2"
														d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
													></path>
												</svg>
												미리보기
											</button>
										</td>
										<td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900">
											<button
												onClick={() =>
													handleEditTicket(ticket)
												}
												className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
											>
												<svg
													className="w-3 h-3 mr-1"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth="2"
														d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
													></path>
												</svg>
												상세보기
											</button>
										</td>
										<td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900">
											<button
												onClick={() =>
													handleOpenMaskingModal(
														ticket
													)
												}
												className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
											>
												<svg
													className="w-3 h-3 mr-1"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth="2"
														d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
													></path>
												</svg>
												티켓 마스킹
											</button>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td
										colSpan="10"
										className="px-4 py-4 text-center text-sm text-gray-500"
									>
										{storeLoading ? (
											"데이터를 불러오는 중..."
										) : (
											<div className="flex flex-col items-center">
												<IoTicketOutline className="h-10 w-10 text-gray-400 mb-2" />
												<p>
													조회된 승차권 정보가 없습니다.
												</p>
												<p className="text-sm">
													날짜를 선택하고 검색해
													주세요.
												</p>
											</div>
										)}
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* 로딩 오버레이 */}
			{(storeLoading || localLoading) && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white p-6 rounded-lg shadow-lg">
						<p className="text-lg">로딩중...</p>
					</div>
				</div>
			)}

			{/* 오류 메시지 */}
			{error && (
				<div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50">
					<span className="block sm:inline">{error}</span>
					<button
						className="absolute top-0 bottom-0 right-0 px-4 py-3"
						onClick={() => setError(null)}
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>
			)}

			{/* 미리보기 모달 */}
			{isPreviewModalOpen && (
				<div
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
					onClick={handleBackdropClick}
				>
					<div className="bg-white p-4 rounded-lg shadow-lg max-w-3xl w-full">
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-medium">
								티켓 미리보기
							</h3>
							<button
								onClick={() => setIsPreviewModalOpen(false)}
								className="text-gray-500 hover:text-gray-700"
							>
								<XCircleIcon className="h-6 w-6" />
							</button>
						</div>
						<div className="flex justify-center">
							{previewImageUrl ? (
								<img
									src={previewImageUrl}
									alt="티켓 이미지"
									className="max-h-[70vh] object-contain"
								/>
							) : (
								<p className="text-gray-500">
									이미지를 불러오는 중...
								</p>
							)}
						</div>
					</div>
				</div>
			)}

			{/* 티켓 마스킹 모달 */}
			<MaskingModal
				isOpen={isMaskingModalOpen}
				onClose={() => setIsMaskingModalOpen(false)}
				ticketId={selectedTicketId}
				imageUrl={selectedTicketImageUrl}
				tickets={ticketsForMasking}
				currentIndex={currentTicketIndex}
				onChangeTicket={(index) => {
					setCurrentTicketIndex(index);
					const ticket = ticketsForMasking[0][index];
					if (ticket) {
						setSelectedTicketId(ticket.ticket_id);
						setSelectedTicketImageUrl(
							ticket.s3_url || ticket.image_url
						);
					}
				}}
			/>

			{/* 승차권 이미지 등록 모달 */}
			<RegisterDetailWithImageModal
				isOpen={isRegisterModalOpen}
				onClose={(refresh = false) => {
					setIsRegisterModalOpen(false);
					if (refresh) {
						handleSearch(startDate, endDate);
					}
				}}
			/>

			{/* 승차권 편집 모달 */}
			<EditTicketModal
				isOpen={isEditModalOpen}
				onClose={(refresh = false) => {
					console.log("refresh", refresh);
					setIsEditModalOpen(false);
					setSelectedTicketForEdit(null);
					// 모달 닫힌 후 티켓 목록 새로고침
					if (refresh) {
						handleSearch(startDate, endDate);
					}
				}}
				ticket={selectedTicketForEdit}
			/>
		</div>
	);
}
